import { useEffect, useState, type CSSProperties } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { resolveFabIconSrc } from '../model/fab-svg';

interface FabIconProps {
  iconUrl?: string;
  /** SVG inline para preview local (sem depender de blob: ou S3). */
  svgContent?: string | null;
  fallbackIcon: LucideIcon;
  backgroundColor?: string;
  iconColor?: string;
  className?: string;
  iconClassName?: string;
}

/** Exibe ícone FAB: SVG inline, proxy BFF ou fallback Lucide. */
export function FabIcon({
  iconUrl,
  svgContent,
  fallbackIcon: FallbackIcon,
  backgroundColor = 'hsl(var(--primary))',
  iconColor,
  className,
  iconClassName,
}: FabIconProps) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [iconUrl, svgContent]);

  const shellStyle = { background: backgroundColor } satisfies CSSProperties;

  if (svgContent) {
    return (
      <span
        className={cn('grid shrink-0 place-items-center overflow-hidden rounded-xl', className)}
        style={shellStyle}
      >
        <span
          className={cn('grid size-[70%] place-items-center [&_svg]:size-full', iconClassName)}
          style={iconColor ? { color: iconColor } : undefined}
          dangerouslySetInnerHTML={{ __html: svgContent }}
        />
      </span>
    );
  }

  if (iconUrl && !imgFailed) {
    const src = resolveFabIconSrc(iconUrl);
    return (
      <span
        className={cn('grid shrink-0 place-items-center overflow-hidden rounded-xl border border-border p-1', className)}
        style={shellStyle}
      >
        <img
          src={src}
          alt=""
          className={cn('size-full object-contain', iconClassName)}
          onError={() => setImgFailed(true)}
        />
      </span>
    );
  }

  return (
    <span
      className={cn('grid shrink-0 place-items-center rounded-xl text-white', className)}
      style={shellStyle}
    >
      <FallbackIcon className={cn('size-5', iconClassName)} />
    </span>
  );
}
